import { useReducer, useState } from 'react'
import { Label, Select } from './elements'
import TBInput from '../../../../Commons/Input.react'
import { TBButton } from '../../../../Commons/Buttons/TBButton.react'
import { ActionType, AddTransactionFormProps, FormState } from './types'
const transactionTypes = [
  'Transfer In',
  'Transfer Out',
  'Buy',
  'Sell',
  'Income',
  'Expense',
]

const initialState = {
  datetime: undefined,
  type: undefined,
  fee: undefined,
  receivedAsset: undefined,
  receivedAmount: undefined,
  sentAsset: undefined,
  sentAmount: undefined,
  price: undefined,
  source: undefined,
}

const determineRequiredFields = (
  type?: string | undefined
): (keyof FormState)[] => {
  switch (type) {
    case 'Transfer In':
    case 'Income':
      return [
        'datetime',
        'type',
        'price',
        'receivedAsset',
        'receivedAmount',
        'source',
      ]
    case 'Transfer Out':
    case 'Expense':
      return ['datetime', 'type', 'price', 'sentAsset', 'sentAmount', 'source']
    default:
      return Object.keys(initialState) as (keyof FormState)[]
  }
}

const addTransactionReducer = (state: FormState, action: ActionType) => ({
  ...state,
  [action.name]: action.value,
})

const AddTransactionForm = ({
  handleSubmit,
  isLoading,
}: AddTransactionFormProps) => {
  const [state, dispatch] = useReducer(addTransactionReducer, initialState)
  const [errors, setErrors] = useState<string[]>([])

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    const { value, name } = e.target
    dispatch({
      value,
      name,
    })
  }

  const validateFields = (requiredFields: (keyof FormState)[]) => {
    const invalidFields = requiredFields.reduce((acc: string[], field) => {
      if (state[field] === undefined) {
        acc.push(`${field} is required`)
      }
      return acc
    }, [])
    return invalidFields
  }

  const transformData = (payload: FormState) => {
    const data = {
      id: '',
      datetime: payload.datetime,
      type: payload.type,
      received: {
        amount: payload.receivedAmount,
        currency: payload.receivedAsset,
      },
      sent: { amount: payload.sentAmount, currency: payload.sentAsset },
      source: payload.source,
      fees: [{ amount: payload.fee, currency: 'USD' }],
      price: { amount: payload.price, currency: 'USD' },
    }

    handleSubmit(data)
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        const requiredFields = determineRequiredFields(state.type)
        const invalidFields = validateFields(requiredFields)
        if (invalidFields.length > 0) {
          setErrors(invalidFields)
        } else {
          transformData(state)
        }
      }}
    >
      <Label>
        Transaction Type
        <Select value={state.type} onChange={handleChange} name="type">
          <option>Select Type</option>
          {transactionTypes.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </Select>
      </Label>
      <TBInput
        value={state.datetime}
        type="date"
        name="datetime"
        label="Date"
        handleChange={handleChange}
      />
      {!['Expense', 'Transfer Out'].includes(state.type ?? '') && (
        <>
          <TBInput
            value={state.receivedAsset}
            name="receivedAsset"
            placeholder="BTC"
            label="Received Currency"
            handleChange={handleChange}
          />
          <TBInput
            value={state.receivedAmount?.toString()}
            name="receivedAmount"
            placeholder="0"
            type="number"
            label="Received Amount"
            handleChange={handleChange}
          />
        </>
      )}
      {!['Income', 'Transfer In'].includes(state.type ?? '') && (
        <>
          <TBInput
            value={state.sentAsset}
            name="sentAsset"
            placeholder="USD"
            label="Sent Currency"
            handleChange={handleChange}
          />
          <TBInput
            value={state.sentAmount?.toString()}
            name="sentAmount"
            placeholder="0"
            type="number"
            label="Sent Amount"
            handleChange={handleChange}
          />
        </>
      )}
      <TBInput
        value={state.price?.toString()}
        name="price"
        placeholder="0 USD"
        type="number"
        label="Price"
        handleChange={handleChange}
      />
      <TBInput
        value={state.fee?.toString()}
        name="fee"
        placeholder="0 USD"
        type="number"
        label="Fee"
        handleChange={handleChange}
      />
      <TBInput
        value={state.source}
        name="source"
        placeholder="Source"
        label="Source"
        handleChange={handleChange}
      />
      <TBButton disabled={isLoading} type="submit">
        Submit
      </TBButton>
      {errors.map((error) => {
        return <p>{error}</p>
      })}
    </form>
  )
}

export default AddTransactionForm
